import { json, redirect } from "@remix-run/node";
import type { ActionFunctionArgs, LoaderFunctionArgs, MetaFunction } from "@remix-run/node";
import { Form, Link, useActionData, useNavigation } from "@remix-run/react";
import { getUser } from "~/lib/session.server";
import { sessionStorage } from "~/lib/session.server";
import { db } from "~/lib/db.server";
import bcrypt from "bcryptjs";
import { BloodhoundLogo } from "~/components/BloodhoundLogo";

export const meta: MetaFunction = () => [
  { title: "Sign in — Bloodhound" },
];

export async function loader({ request }: LoaderFunctionArgs) {
  const user = await getUser(request);
  if (user) throw redirect("/dashboard");
  return {};
}

export async function action({ request }: ActionFunctionArgs) {
  const form = await request.formData();
  const email = (form.get("email") as string)?.trim().toLowerCase();
  const password = form.get("password") as string;

  if (!email || !password) {
    return json({ error: "Email and password are required." }, { status: 400 });
  }

  const user = await db.workspaceUser.findFirst({ where: { email } });

  if (!user || !user.passwordHash) {
    return json({ error: "Invalid email or password." }, { status: 401 });
  }

  const valid = await bcrypt.compare(password, user.passwordHash);
  if (!valid) {
    return json({ error: "Invalid email or password." }, { status: 401 });
  }

  const session = await sessionStorage.getSession(request.headers.get("Cookie"));
  session.set("workspaceUserId", user.id);

  return redirect("/dashboard", {
    headers: { "Set-Cookie": await sessionStorage.commitSession(session) },
  });
}

export default function Login() {
  const actionData = useActionData<typeof action>();
  const navigation = useNavigation();
  const submitting = navigation.state === "submitting";

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-[#080808] relative overflow-hidden">
      {/* Radar */}
      <div className="radar-sweep" />
      <div className="radar-rings">
        <span /><span /><span /><span />
      </div>

      {/* Radial glow behind card */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: "radial-gradient(ellipse 600px 500px at 50% 55%, rgba(220,38,38,0.04) 0%, transparent 70%)",
        }}
      />

      {/* Dot grid */}
      <div
        className="absolute inset-0"
        style={{
          backgroundImage: "radial-gradient(circle, rgba(255,255,255,0.04) 1px, transparent 1px)",
          backgroundSize: "24px 24px",
        }}
      />

      <div className="relative z-10 w-full max-w-sm px-6">
        {/* Logo */}
        <div className="flex flex-col items-center mb-10">
          <BloodhoundLogo size="lg" />
          <p className="mt-4 text-gray-400 text-sm text-center leading-relaxed">
            Monitor every Shopify store.<br />From one place.
          </p>
        </div>

        {/* Sign-in card */}
        <div className="bg-white/[0.04] border border-white/[0.08] rounded-lg p-7 shadow-2xl shadow-black/40 backdrop-blur-md">
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider text-center mb-5">
            Sign in to continue
          </p>

          {/* Google */}
          <form action="/auth/google" method="post">
            <button
              type="submit"
              className="w-full flex items-center justify-center gap-3 bg-white text-gray-900 font-semibold text-sm px-4 py-3.5 rounded-xl hover:bg-gray-50 active:bg-gray-100 transition-colors duration-150 shadow-sm focus-visible:ring-2 focus-visible:ring-white/30 focus-visible:outline-none"
            >
              <GoogleIcon />
              Continue with Google
            </button>
          </form>

          {/* Divider */}
          <div className="flex items-center gap-3 my-5">
            <div className="flex-1 h-px bg-white/[0.08]" />
            <span className="text-xs text-gray-600">or</span>
            <div className="flex-1 h-px bg-white/[0.08]" />
          </div>

          {/* Email / password form */}
          <Form method="post" className="space-y-3">
            {actionData?.error && (
              <p className="text-xs text-red-400 text-center bg-red-950/30 border border-red-800/30 rounded-lg px-3 py-2">
                {actionData.error}
              </p>
            )}
            <div>
              <input
                name="email"
                type="email"
                placeholder="Email"
                autoComplete="email"
                required
                className="w-full bg-white/[0.04] border border-white/[0.08] rounded-xl px-4 py-3 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-white/20 focus:ring-1 focus:ring-white/10 transition-colors"
              />
            </div>
            <div>
              <input
                name="password"
                type="password"
                placeholder="Password"
                autoComplete="current-password"
                required
                className="w-full bg-white/[0.04] border border-white/[0.08] rounded-xl px-4 py-3 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-white/20 focus:ring-1 focus:ring-white/10 transition-colors"
              />
            </div>
            <button
              type="submit"
              disabled={submitting}
              className="w-full bg-[#dc2626] text-white font-semibold text-sm px-4 py-3.5 rounded-xl hover:bg-[#b91c1c] active:bg-[#991b1b] transition-colors duration-150 disabled:opacity-60 focus-visible:ring-2 focus-visible:ring-red-500/50 focus-visible:outline-none"
            >
              {submitting ? "Signing in…" : "Sign in"}
            </button>
          </Form>

          <div className="mt-4 flex items-center justify-between text-xs">
            <Link to="/forgot-password" className="text-gray-500 hover:text-gray-400 underline underline-offset-2">
              Forgot password?
            </Link>
            <Link to="/register" className="text-gray-400 hover:text-gray-300 underline underline-offset-2">
              Create account
            </Link>
          </div>

          <p className="mt-5 text-center text-xs text-gray-500 leading-relaxed">
            By signing in you agree to our{" "}
            <a
              href="/terms"
              className="text-gray-400 hover:text-gray-300 underline underline-offset-2"
            >
              Terms
            </a>{" "}
            and{" "}
            <a
              href="/privacy"
              className="text-gray-400 hover:text-gray-300 underline underline-offset-2"
            >
              Privacy Policy
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}

function GoogleIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 18 18"
      aria-hidden="true"
      className="shrink-0"
    >
      <path
        fill="#4285F4"
        d="M16.51 8H8.98v3h4.3c-.18 1-.74 1.48-1.6 2.04v2.01h2.6a7.8 7.8 0 0 0 2.38-5.88c0-.57-.05-.66-.15-1.18z"
      />
      <path
        fill="#34A853"
        d="M8.98 17c2.16 0 3.97-.72 5.3-1.94l-2.6-2a4.8 4.8 0 0 1-7.18-2.54H1.83v2.07A8 8 0 0 0 8.98 17z"
      />
      <path
        fill="#FBBC05"
        d="M4.5 10.52a4.8 4.8 0 0 1 0-3.04V5.41H1.83a8 8 0 0 0 0 7.18l2.67-2.07z"
      />
      <path
        fill="#EA4335"
        d="M8.98 4.18c1.17 0 2.23.4 3.06 1.2l2.3-2.3A8 8 0 0 0 1.83 5.4L4.5 7.49a4.77 4.77 0 0 1 4.48-3.3z"
      />
    </svg>
  );
}
